import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Thumbs, Zoom, FreeMode } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';
import { Product } from '../../types';
import { useTranslation } from 'react-i18next';
import './ProductDetailModal.css';

interface ProductDetailModalProps {
    product: Product;
    isOpen: boolean;
    onClose: () => void;
}

const ProductDetailModal: React.FC<ProductDetailModalProps> = ({ product, isOpen, onClose }) => {
    const { i18n, t } = useTranslation();
    const [thumbsSwiper, setThumbsSwiper] = useState<SwiperType | null>(null);
    const [selectedTab, setSelectedTab] = useState<'description' | 'nutrition' | 'ingredients'>('description');
    const [quantity, setQuantity] = useState(1);
    const [activeImageIndex, setActiveImageIndex] = useState(0);

    const currentLang = i18n.language as 'en' | 'ru' | 'kk';

    const handleQuantityChange = (type: 'increase' | 'decrease') => {
        if (type === 'increase') {
            setQuantity(prev => prev + 1);
        } else if (type === 'decrease' && quantity > 1) {
            setQuantity(prev => prev - 1);
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                className="product-modal-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={onClose}
            >
                <motion.div
                    className="product-modal-container"
                    initial={{ opacity: 0, scale: 0.9, y: 50 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9, y: 50 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Close Button */}
                    <button className="modal-close-btn" onClick={onClose}>
                        ✕
                    </button>

                    <div className="modal-content-wrapper">
                        {/* Left Side - Gallery */}
                        <div className="modal-gallery">
                            {product.featured && (
                                <div className="modal-featured-badge">
                                    ⭐ Featured
                                </div>
                            )}

                            <Swiper
                                modules={[Navigation, Pagination, Thumbs, Zoom]}
                                spaceBetween={10}
                                navigation={true}
                                pagination={{ clickable: true, dynamicBullets: true }}
                                thumbs={{ swiper: thumbsSwiper && !thumbsSwiper.destroyed ? thumbsSwiper : null }}
                                zoom={{ maxRatio: 3, minRatio: 1 }}
                                onSlideChange={(swiper: SwiperType) => setActiveImageIndex(swiper.activeIndex)}
                                className="modal-main-swiper"
                            >
                                {product.images.map((image, index) => (
                                    <SwiperSlide key={index}>
                                        <div className="swiper-zoom-container">
                                            <img src={image} alt={`${product.name[currentLang]} ${index + 1}`} />
                                        </div>
                                    </SwiperSlide>
                                ))}
                            </Swiper>

                            {product.images.length > 1 && (
                                <Swiper
                                    onSwiper={setThumbsSwiper}
                                    modules={[FreeMode, Thumbs]}
                                    spaceBetween={8}
                                    slidesPerView={4}
                                    freeMode={true}
                                    watchSlidesProgress={true}
                                    className="modal-thumbs-swiper"
                                >
                                    {product.images.map((image, index) => (
                                        <SwiperSlide key={index}>
                                            <img src={image} alt={`Thumbnail ${index + 1}`} />
                                        </SwiperSlide>
                                    ))}
                                </Swiper>
                            )}

                            <div className="modal-image-info">
                                📷 {activeImageIndex + 1} / {product.images.length}
                            </div>
                        </div>

                        {/* Right Side - Info */}
                        <div className="modal-product-info">
                            <div className="modal-header">
                                <span className="modal-category-tag">
                                    🍽️ {product.category}
                                </span>
                                <h2 className="modal-product-title">{product.name[currentLang]}</h2>

                                <div className="modal-rating">
                                    <span className="modal-stars">★★★★★</span>
                                    <span className="modal-rating-text">4.9 (156 {t('products.reviews')})</span>
                                </div>
                            </div>

                            <p className="modal-description">{product.description[currentLang]}</p>

                            {/* Key Features */}
                            <div className="modal-features">
                                <div className="modal-feature-item">
                                    <span className="modal-feature-icon">🥗</span>
                                    <span>{t('products.fresh_ingredients')}</span>
                                </div>
                                <div className="modal-feature-item">
                                    <span className="modal-feature-icon">✅</span>
                                    <span>{t('products.halal_certified')}</span>
                                </div>
                                <div className="modal-feature-item">
                                    <span className="modal-feature-icon">👨‍🍳</span>
                                    <span>{t('products.premium_quality')}</span>
                                </div>
                            </div>

                            {/* Quantity */}
                            <div className="modal-quantity-section">
                                <label>{t('products.quantity')}:</label>
                                <div className="modal-quantity-controls">
                                    <button onClick={() => handleQuantityChange('decrease')} disabled={quantity <= 1}>
                                        −
                                    </button>
                                    <span>{quantity}</span>
                                    <button onClick={() => handleQuantityChange('increase')}>
                                        +
                                    </button>
                                </div>
                            </div>

                            {/* Tabs */}
                            <div className="modal-tabs">
                                <button
                                    className={`modal-tab ${selectedTab === 'description' ? 'active' : ''}`}
                                    onClick={() => setSelectedTab('description')}
                                >
                                    📋 {t('products.description')}
                                </button>
                                <button
                                    className={`modal-tab ${selectedTab === 'nutrition' ? 'active' : ''}`}
                                    onClick={() => setSelectedTab('nutrition')}
                                >
                                    🥗 {t('products.nutrition_facts')}
                                </button>
                                <button
                                    className={`modal-tab ${selectedTab === 'ingredients' ? 'active' : ''}`}
                                    onClick={() => setSelectedTab('ingredients')}
                                >
                                    🧪 {t('products.ingredients')}
                                </button>
                            </div>

                            <div className="modal-tab-content">
                                {selectedTab === 'description' && (
                                    <div className="modal-tab-panel">
                                        <p className="modal-description-text">{product.description[currentLang]}</p>
                                        <ul className="modal-benefits-list">
                                            <li>✓ {t('products.chef_made')}</li>
                                            <li>✓ {t('products.temperature_controlled')}</li>
                                            <li>✓ {t('products.quality_certified')}</li>
                                            <li>✓ {t('products.fresh_guarantee')}</li>
                                        </ul>
                                    </div>
                                )}

                                {selectedTab === 'nutrition' && (
                                    <div className="modal-tab-panel">
                                        {product.nutrition ? (
                                            <div className="modal-nutrition-grid">
                                                {Object.entries(product.nutrition).map(([key, value]) => (
                                                    <div key={key} className="modal-nutrition-item">
                                                        <span className="label">{t(`products.${key}`)}</span>
                                                        <span className="value">{value}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p>{t('products.nutrition_facts')} {t('products.reviews_coming_soon')}</p>
                                        )}
                                    </div>
                                )}

                                {selectedTab === 'ingredients' && (
                                    <div className="modal-tab-panel">
                                        {product.allergens && product.allergens.length > 0 ? (
                                            <>
                                                <p className="modal-ingredients-text">
                                                    {product.allergens.join(', ')}
                                                </p>
                                                <div className="modal-allergen-warning">
                                                    ⚠️ <strong>{t('products.ingredients')}:</strong> {product.allergens.join(', ')}
                                                </div>
                                            </>
                                        ) : (
                                            <p className="modal-ingredients-text">{t('products.product_description_detail')}</p>
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="modal-actions">
                                <button className="modal-btn-primary">
                                    📧 {t('products.send_inquiry')}
                                </button>
                                <button className="modal-btn-secondary">
                                    🔗 {t('products.share')}
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default ProductDetailModal;
